import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useParams} from "react-router-dom";
import {Builder, Query, Utils as QbUtils} from '@react-awesome-query-builder/ui';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import ListItemText from '@mui/material/ListItemText';
import Checkbox from '@mui/material/Checkbox';
import Box from '@mui/material/Box';
import ListSubheader from '@mui/material/ListSubheader';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Tags from "@yaireo/tagify/dist/react.tagify";

import CustomFormControl from "../../Dropdown/CustomFormControl";
import QueryBuilderConfig from "./QueryBuilderConfig";
import CustomButtonWithIcon from "../../../../components/CustomButtonWithIcon/Buttons";
import AlertLevelSelector from "../FormComponents/AlertLevelSelector";
import HotelSelection from "../FormComponents/HotelSelection";
import GroupSelection from "../FormComponents/GroupSelection";

import {APP_ALERT_CENTER_API_ENDPOINT} from "../../../../constants/api_urls";
import {getUserToken} from "utils/functions/userToken";
import {getUserDetailsFromToken} from "utils/functions/userTokenDetails";
import {buildFieldMap, sqlToJsonLogic, transformFieldNames} from '../../Functions/index';

import '../AlertForm.scss';
import '@react-awesome-query-builder/ui/css/styles.css';
import '@react-awesome-query-builder/ui/css/compact_styles.css';

const AlertCampaignBaseForm = ({ apiEndpoint, actionType, campaignType, alertType, submitCallback, alertData, availableSegments, onClose, showSnackbar }) => {
    const { idaccount } = useParams();
    const jwtToken = getUserToken();
    const [userDetails, setUserDetails] = useState({ isAdminUser: false, isSuperAdminUser: false, isMainUser: false });

    const [alertName, setAlertName] = useState(alertData?.name || '');
    const [segmentList, setSegmentList] = useState(alertData?.segmentList || []);
    const [alertRecipients, setAlertRecipients] = useState(alertData?.recipients || []);
    const [additionnalColumns, setAdditionnalColumns] = useState(alertData?.additionnalColumns || []);
    const [alertPeriod, setAlertPeriod] = useState(alertData?.alertPeriod || 1);
    const [alertLevel, setAlertLevel] = useState(alertData?.alertLevel || "ACCOUNT");
    const [alertCondition, setAlertCondition] = useState(alertData?.alertCondition || null);
    const [tree, setTree] = useState(null);
    const [optionsForAdditionalColumns, setOptionsForAdditionalColumns] = useState([]);
    const [fieldMap, setFieldMap] = useState({});
    const [config, setConfig] = useState(QueryBuilderConfig);
    const [errors, setErrors] = useState({ alertName: false, alertPeriod: false, alertRecipients: false, alertCondition: false, additionnalColumns: false });

    const [isHotelSlidingPanelOpen, setIsHotelSlidingPanelOpen] = useState(false);
    const [isGroupSlidingPanelOpen, setIsGroupSlidingPanelOpen] = useState(false);
    const [selectedHotels, setSelectedHotels] = useState([]);
    const [hotelRowSelection, setHotelRowSelection] = useState({});
    const [selectedGroups, setSelectedGroups] = useState([]);
    const [groupRowSelection, setGroupRowSelection] = useState({});
    const [errorSelectedHotels, setErrorSelectedHotels] = useState(false);
    const [errorSelectedGroups, setErrorSelectedGroups] = useState(false);
    const tagifyRef = useRef(null);

    const isAdminOrMainUser = userDetails.isAdminUser || userDetails.isSuperAdminUser || userDetails.isMainUser;

    useEffect(() => {
        const token = getUserToken();
        const { UserDetails } = getUserDetailsFromToken(token);
        setUserDetails(UserDetails);
    }, []);

    const handleSegmentChange = (e) => {
        const value = e.target.value;
        setSegmentList(prev => prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]);
    };

    const handleRecipientsChange = useCallback((e) => {
        let parsedValue;
        try {
            parsedValue = JSON.parse(e.detail.value);
        } catch {
            parsedValue = [];
        }
        setAlertRecipients(parsedValue.map(tag => tag.value));
    }, []);

    const handleHotelSelect = useCallback((hotels) => setSelectedHotels(hotels), []);
    const handleGroupSelect = useCallback((groups) => setSelectedGroups(groups), []);

    const handleRemoveHotel = (hotelId) => {
        setSelectedHotels(prev => prev.filter(h => h.id !== hotelId));
        setHotelRowSelection(prev => { const copy = { ...prev }; delete copy[hotelId]; return copy; });
    };
    const handleRemoveGroups = (groupId) => {
        setSelectedGroups(prev => prev.filter(g => g.id !== groupId));
        setGroupRowSelection(prev => { const copy = { ...prev }; delete copy[groupId]; return copy; });
    };

    const handleClearAllHotels = () => { setSelectedHotels([]); setHotelRowSelection({}); };
    const handleClearAllGroups = () => { setSelectedGroups([]); setGroupRowSelection({}); };

    const generateFieldsFromMetrics = (metrics) => {
        const groupedMetrics = metrics.reduce((acc, metric) => {
            const { metric_group } = metric;
            acc[metric_group] = acc[metric_group] || [];
            acc[metric_group].push(metric);
            return acc;
        }, {});
        const fields = {};
        for (const group in groupedMetrics) {
            const subfields = {};
            groupedMetrics[group].forEach(({ metric_label, metric_column }) => {
                subfields[metric_column] = {
                    fieldSettings: { groupTitle: group },
                    label: metric_label,
                    type: 'number',
                    valueSources: ['value'],
                    preferWidgets: ['number'],
                    operators: ['equal', 'less', 'greater', 'less_or_equal', 'greater_or_equal'],
                    widgets: {
                        number: {
                            operators: ['equal', 'less', 'greater', 'less_or_equal', 'greater_or_equal'],
                        },
                    },
                };
            });
            fields[group] = { type: '!struct', label: group, subfields };
        }
        return fields;
    };

    useEffect(() => {
        const fetchMetrics = async () => {
            try {
                const res = await fetch(apiEndpoint, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${jwtToken}`,
                        'Content-Type': 'application/json',
                    },
                });
                const result = await res.json();
                const fields = generateFieldsFromMetrics(result.metric_name || []);
                setConfig(prev => ({
                    ...QueryBuilderConfig,
                    fields,
                    settings: {
                        ...prev.settings,
                        showNot: false,
                        canReorder: false,
                        groupOperators: ['AND', 'OR'],
                        forceShowConj: true,
                        formatField: (f) => f.split(".").pop()
                    }
                }));
                setFieldMap(buildFieldMap(fields));
                setOptionsForAdditionalColumns(result.metric_name || []);
            } catch (e) {
                console.error(e);
            }
        };
        fetchMetrics();
    }, [apiEndpoint, jwtToken]);

    useEffect(() => {
        if (alertCondition && config.fields && Object.keys(config.fields).length > 0) {
            const rawLogic = sqlToJsonLogic(alertCondition);
            const transformed = transformFieldNames(rawLogic, fieldMap);
            const loadedTree = QbUtils.loadFromJsonLogic(transformed, config);
            setTree(loadedTree);
        } else {
            const emptyTree = QbUtils.checkTree(QbUtils.loadTree({ id: QbUtils.uuid(), type: 'group' }), config);
            setTree(emptyTree);
        }
    }, [alertCondition, config, fieldMap]);

    useEffect(() => {
        const fetchLinkedHotels = async () => {
            if (alertData && actionType === 'edit' && alertLevel === 'HOTEL') {
                try {
                    const res = await fetch(`${APP_ALERT_CENTER_API_ENDPOINT}/api/alert-center-metrics/list-linked-hotels/${alertData.id}`, {
                        method: 'GET',
                        headers: {
                            'Authorization': `Bearer ${jwtToken}`,
                            'Content-Type': 'application/json',
                        }
                    });
                    const hotels = await res.json();
                    setSelectedHotels(hotels);
                    const selectionMap = {};
                    hotels.forEach(hotel => {
                        selectionMap[hotel.id] = true;
                    });
                    setHotelRowSelection(selectionMap);
                } catch (error) {
                    console.error("Error fetching linked hotels:", error);
                }
            }
        };

        fetchLinkedHotels();
    }, [alertData, actionType, alertLevel]);

    useEffect(() => {
        const fetchLinkedGroups = async () => {
            if (alertData && actionType === 'edit' && alertLevel === 'GROUP') {
                try {
                    const res = await fetch(`${APP_ALERT_CENTER_API_ENDPOINT}/api/alert-center-metrics/list-linked-groups/${alertData.id}`, {
                        method: 'GET',
                        headers: {
                            'Authorization': `Bearer ${jwtToken}`,
                            'Content-Type': 'application/json',
                        }
                    });
                    const groups = await res.json();
                    setSelectedGroups(groups);

                    const selectionMap = {};
                    groups.forEach(group => {
                        selectionMap[group.id] = true;
                    });
                    setGroupRowSelection(selectionMap);
                } catch (error) {
                    console.error("Error fetching linked groups:", error);
                }
            }
        };

        fetchLinkedGroups();
    }, [alertData, actionType, alertLevel]);


    useEffect(() => {
        if (alertLevel === "HOTEL") {
            setSelectedGroups([]);
            setGroupRowSelection([]);
        } else if (alertLevel === "GROUP") {
            setSelectedHotels([]);
            setHotelRowSelection([]);
        } else {
            setSelectedGroups([]);
            setGroupRowSelection([]);
            setSelectedHotels([]);
            setHotelRowSelection([]);
        }
    }, [alertLevel]);
    const onChange = (immutableTree) => setTree(immutableTree);

    const handleSubmit = () => {
        const sqlFormat = QbUtils.sqlFormat(tree, config);
        const body = {
            alert_name: alertName,
            segment_list: segmentList,
            alert_recipients: alertRecipients,
            additionnal_columns: additionnalColumns || [],
            alert_condition: sqlFormat,
            alert_period: alertPeriod,
            alert_level: alertLevel,
            alert_hotels_selection: selectedHotels.map(h => h.id),
            alert_groups_selection: selectedGroups.map(g => g.id),
        };

        const missingHotels = alertLevel === "HOTEL" && selectedHotels.length === 0;
        const missingGroups = alertLevel === "GROUP" && selectedGroups.length === 0;

        const newErrors = {
            alertName: !body.alert_name,
            alertPeriod: !body.alert_period,
            alertRecipients: body.alert_recipients.length === 0,
            alertCondition: !body.alert_condition,
            additionnalColumns: body.additionnal_columns.length === 0,
        };
        setErrors(newErrors);
        setErrorSelectedHotels(missingHotels);
        setErrorSelectedGroups(missingGroups);

        if (Object.values(newErrors).some(e => e) || missingHotels || missingGroups) {
            showSnackbar('Please fill in all required fields', 'error');
        } else {
            submitCallback(body);
        }
    };

    return (
        <div className="alert-form">
            <div className="top-side">
                <TextField
                    placeholder="Alert Name:"
                    variant="standard"
                    value={alertName}
                    required
                    error={errors.alertName}
                    helperText={errors.alertName ? "This field is required" : ""}
                    onChange={(e) => setAlertName(e.target.value)}
                    sx={{
                        '& ::placeholder': { fontSize: '12px' },
                        '& .MuiInputBase-input': {
                            fontSize: '12px',
                        },
                        width: "55%"
                    }}
                />

            </div>
            <AlertLevelSelector
                value={alertLevel}
                onChange={(e) => setAlertLevel(e.target.value)}
                isAdminOrMainUser={isAdminOrMainUser}
                error={errors.alertLevel}
            />

            {alertLevel === "HOTEL" && (
                <HotelSelection
                    isOpen={isHotelSlidingPanelOpen}
                    setOpen={setIsHotelSlidingPanelOpen}
                    selectedHotels={selectedHotels}
                    handleRemoveHotel={handleRemoveHotel}
                    handleClearAllHotels={handleClearAllHotels}
                    handleHotelSelect={handleHotelSelect}
                    hotelRowSelection={hotelRowSelection}
                    setHotelRowSelection={setHotelRowSelection}
                    idaccount={idaccount}
                    jwtToken={jwtToken}
                    showError={errorSelectedHotels}
                />
            )}

            {alertLevel === "GROUP" && isAdminOrMainUser && (

                <GroupSelection
                    isOpen={isGroupSlidingPanelOpen}
                    setOpen={setIsGroupSlidingPanelOpen}
                    selectedGroups={selectedGroups}
                    handleRemoveGroup={handleRemoveGroups}
                    handleClearAllGroups={handleClearAllGroups}
                    handleGroupSelect={handleGroupSelect}
                    groupRowSelection={groupRowSelection}
                    setGroupRowSelection={setGroupRowSelection}
                    idaccount={idaccount}
                    jwtToken={jwtToken}
                    showError={errorSelectedGroups}
                />
            )}

            <div className="bottom-top-side">
                <div className="alert-period-container">
                    <label>ALERT PERIOD</label>
                    <CustomFormControl
                        value={alertPeriod}
                        onChange={(e) => setAlertPeriod(e.target.value)}
                        displayEmpty
                        renderValue={(selected) => {
                            if (!selected) {
                                return (
                                    <span style={{
                                        fontSize: "12px",
                                        color: "#bab7b7"
                                    }}>Number of Days</span>
                                );
                            }
                            if (selected === -1) {
                                return "Current Month";
                            }
                            return selected+ " day(s)";
                        }}
                        required
                        error={errors.alertPeriod}
                        helperText={errors.alertPeriod ? "This field is required" : ""}
                    >
                        <MenuItem disabled value="">
                            <span style={{
                                fontSize: "12px",
                                color: "black",
                                fontFamily: "Inter",
                                marginLeft: "4px"
                            }}>Number of Days</span>
                        </MenuItem>


                        {Array.from({ length: 10 }, (_, i) => i + 1).map((num) => (
                            <MenuItem key={num} value={num} sx={{ fontSize: '12px', fontFamily: "Inter", color: "black" }}>
                                {num}
                            </MenuItem>
                        ))}
                        <MenuItem key={-1} value={-1} sx={{ fontSize: '12px', fontFamily: "Inter", color: "black" }}>
                            Current Month
                        </MenuItem>
                    </CustomFormControl>


                </div>
                {availableSegments && availableSegments.length > 0 && (
                    <div className="segment-list-container">
                        <div className="segment-list-label">
                            <label>VIEW BY</label>
                        </div>
                        <div className="segment-list-checkboxes">
                            <FormGroup>
                                {availableSegments.includes('DEVICE') && (
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                size="small"
                                                sx={{
                                                    paddingBlock: "2px",
                                                    color: "#BAB7B7",
                                                    "&.Mui-checked": {
                                                        color: "#61D5A9",
                                                    },
                                                    "& .MuiSvgIcon-root": {
                                                        height: "12px",
                                                        width: "12px",
                                                    },
                                                }}
                                                onChange={handleSegmentChange}
                                                checked={segmentList?.includes("DEVICE") || false}
                                                value={"DEVICE"}
                                            />
                                        }
                                        sx={{
                                            alignItems: "flex-start",
                                            "& .MuiFormControlLabel-label": {
                                                color: "black",
                                                fontSize: "12px",
                                                fontFamily: "Inter",
                                                fontWeight: 400,
                                                marginLeft: "8px"
                                            },
                                        }}
                                        label="Device"
                                        className="custom-checkbox-label"
                                    />
                                )}
                                {availableSegments.includes('MARKET') && (
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                size="small"
                                                sx={{
                                                    paddingBlock: "2px",
                                                    color: "#BAB7B7",
                                                    "&.Mui-checked": {
                                                        color: "#61D5A9",
                                                    },
                                                    "& .MuiSvgIcon-root": {
                                                        height: "12px",
                                                        width: "12px",
                                                    },
                                                }}
                                                onChange={handleSegmentChange}
                                                checked={segmentList?.includes("MARKET") || false}
                                                value={"MARKET"}
                                            />
                                        }
                                        sx={{
                                            alignItems: "flex-start",
                                            "& .MuiFormControlLabel-label": {
                                                color: "black",
                                                fontSize: "12px",
                                                fontFamily: "Inter",
                                                fontWeight: 400,
                                                marginLeft: "8px"
                                            },
                                        }}
                                        label="Market"
                                        className="custom-checkbox-label"
                                    />
                                )}
                            </FormGroup>
                        </div>
                    </div>

                )}

                <div className="additional-columns-container">
                    <label>ADDITIONAL COLUMNS</label>
                    <CustomFormControl
                        multiple
                        value={additionnalColumns || []}
                        onChange={(event) => setAdditionnalColumns(event.target.value)}
                        displayEmpty
                        renderValue={(selected) => {
                            if (selected.length === 0) {
                                return (
                                    <span style={{ fontSize: "12px", fontFamily: "Inter", color: "black" }}>
                    Choose Columns
                </span>
                                );
                            }
                            return selected
                                .map((value) => {
                                    const option = optionsForAdditionalColumns.find(
                                        (opt) => opt.metric_name === value
                                    );
                                    return option ? option.metric_label : value;
                                })
                                .join(", ");
                        }}
                        required
                        error={errors.additionnalColumns}
                        helperText={errors.additionnalColumns ? "This field is required" : ""}
                        size="small"
                        sx={{ width: "280px" }}
                        MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
                    >
                        <ListSubheader
                            style={{
                                fontSize: "12px",
                                fontFamily: "Inter",
                                color: "#6A76DB",
                                lineHeight: '20px',
                            }}
                        >
                            Basic Metrics
                        </ListSubheader>
                        {optionsForAdditionalColumns
                            .filter(option => option.metric_group === "Metrics")
                            .map((option) => (
                                <MenuItem
                                    key={option.metric_name}
                                    value={option.metric_name}
                                    sx={{ fontSize: "12px", color: "black", fontFamily: "Inter" }}
                                >
                                    <Checkbox
                                        checked={additionnalColumns.includes(option.metric_name)}
                                        sx={{
                                            "& .MuiSvgIcon-root": {
                                                fontSize: 12,
                                            },
                                        }}
                                    />
                                    <ListItemText
                                        primary={option.metric_label}
                                        primaryTypographyProps={{
                                            style: { fontSize: "12px", fontFamily: "Inter", color: "black" },
                                        }}
                                    />
                                </MenuItem>
                            ))}
                        <ListSubheader
                            style={{
                                fontSize: "12px",
                                fontFamily: "Inter",
                                color: "#6A76DB",
                                lineHeight: '20px',
                            }}
                        >
                            Evolution Metrics
                        </ListSubheader>
                        {optionsForAdditionalColumns
                            .filter(option => option.metric_group === "Metrics Evolution")
                            .map((option) => (
                                <MenuItem
                                    key={option.metric_name}
                                    value={option.metric_name}
                                    sx={{ fontSize: "12px", color: "black", fontFamily: "Inter" }}
                                >
                                    <Checkbox
                                        checked={additionnalColumns.includes(option.metric_name)}
                                        sx={{
                                            "& .MuiSvgIcon-root": {
                                                fontSize: 12,
                                            },
                                        }}
                                    />
                                    <ListItemText
                                        primary={option.metric_label}
                                        primaryTypographyProps={{
                                            style: { fontSize: "12px", fontFamily: "Inter", color: "black" },
                                        }}
                                    />
                                </MenuItem>
                            ))}
                    </CustomFormControl>


                </div>

            </div>

            <div className="query-builder-container">
                <label>METRIC ALERT RULES *</label>
                <div className="query-builder">
                    {(segmentList.includes('DEVICE') || segmentList.includes('MARKET')) && (
                        <div style={{
                            fontSize: '12px',
                            fontFamily: 'Inter',
                            color: '#6A76DB',
                            marginTop: '4px',
                        }}>
                            Impression metrics are unavailable when either the DEVICE or MARKET segment is selected.
                        </div>
                    )}

                    {tree && (
                        <Query
                            {...config}
                            value={tree || null}
                            onChange={onChange}
                            renderBuilder={(props) => <Builder {...props} />}
                        />
                    )}
                </div>
            </div>


            <div className="bottom-side">
                <label>RECIPIENTS *</label>
                <div className="email-container">
                    <Box sx={{ display: "flex", gap: "3px", flexDirection: "column" }}>
                        <Tags
                            ref={tagifyRef}
                            whitelist={alertRecipients}
                            placeholder='Recipients'
                            settings={{
                                pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                            }}
                            onChange={handleRecipientsChange}
                            defaultValue={Array.isArray(alertRecipients) ? alertRecipients.join(',') : ""}
                            className={errors.alertRecipients ? 'error-tagify' : ''}
                        />
                        {errors.alertRecipients && <span style={{ color: '#d32f2f', fontSize: '12px' }}>This field is required</span>}
                    </Box>
                </div>
            </div>

            <div className="button-container">
                <CustomButtonWithIcon
                    color="#b0acac"
                    hoverColor="#12794F"
                    titleColor="#373F41"
                    hoverTitleColor={"white"}
                    width={"120px"}
                    height={"24px"}
                    borderRadius="20px"
                    textStyle={{
                        fontFamily: 'Inter',
                        fontSize: '12px',
                        fontWeight: 500,
                        lineHeight: '14.52px',
                        textAlign: 'center',
                    }}
                    onClick={handleSubmit}

                >
                    Save Alert
                </CustomButtonWithIcon>

                <CustomButtonWithIcon
                    color="#b0acac"
                    hoverColor="#12794F"
                    titleColor="#373F41"
                    hoverTitleColor={"white"}
                    width={"120px"}
                    height={"24px"}
                    borderRadius="20px"
                    textStyle={{
                        fontFamily: 'Inter',
                        fontSize: '12px',
                        fontWeight: 500,
                        lineHeight: '14.52px',
                        textAlign: 'center',
                    }}
                    onClick={onClose}

                >
                    Cancel
                </CustomButtonWithIcon>
            </div>
        </div>
    );
};

export default AlertCampaignBaseForm;
